import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Drill, Truck, Factory, ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";

export const ChevronCallForSolutionsDemo = () => {
  const navigate = useNavigate();

  const upstreamCfS = [
    "Improving Lost Circulation Management in Unconventional Wells",
    "Call for Lessons Learned: Kick Detection and Well Control Events", 
    "Optimizing Horizontal Wellbore Cleaning Strategies",
    "AI-Ready Framework for Real-Time Drilling Performance Metrics",
    "Knowledge Base on Enhanced Oil Recovery (EOR) Pilot Learnings",
    "Seismic-to-Reservoir Workflow Best Practices in Shale Plays",
    "Reducing Non-Productive Time (NPT) Through Workflow Standardization",
    "Fracture Stimulation Design Optimization Based on Field Experience",
    "Knowledge Transfer on Aging Well Re-Entry and Recompletion Techniques",
    "Developing a Safety Protocol KB for HPHT Drilling Environments"
  ];

  const midstreamCfS = [
    "Leak Detection & Response Procedures for Aging Pipelines",
    "Best Practices for SCADA System Integration Across Assets",
    "Call for Insights: Pipeline Pigging Optimization Strategies",
    "Digital Twin Readiness: Pipeline Asset Tagging and Data Standards",
    "Reducing Downtime in Crude Oil Pumping Stations",
    "Knowledge Base on Regulatory Compliance in Cross-Border Transport",
    "Emergency Response Procedures for Tank Farms and Storage Facilities",
    "Smart Contract Templates for Vendor Logistics Coordination",
    "Carbon Emission Tracking Protocols in Midstream Operations",
    "Training Content for Field Techs on Valve Inspection and Repair"
  ];

  const downstreamCfS = [
    "Turnaround Optimization: Knowledge Base on Shutdown/Startup Sequences",
    "Improving Process Safety in Fluid Catalytic Cracking (FCC) Units",
    "AI-Driven Yield Optimization for Multi-Product Refineries",
    "Best Practices for Hydrogen Blending in Natural Gas Streams",
    "Emission Reduction Protocols for Flares and Vents",
    "Maintenance Knowledge Base for High-Temperature Heat Exchangers",
    "Digital SOPs for Refinery Lab Testing and QA/QC",
    "Call for Solutions: AI-Based Root Cause Analysis for Refinery Incidents",
    "Retail Fuel Distribution Optimization in High-Demand Regions",
    "KB for Training Station Operators on EV and Traditional Fuel Coexistence"
  ];

  const handleCfSClick = (callTitle: string, domain: string) => {
    navigate("/call-for-solutions/bounty", {
      state: {
        selectedArea: "Chevron Energy Operations",
        chevronCall: {
          title: callTitle,
          organization: "Chevron",
          bountyAmount: "10000",
          bountyType: "Enterprise",
          contributors: ["Trey Fleming", "Karen Watson", "Pete Dumont", "Giblet"],
          domain: domain,
          description: generateDescription(callTitle, domain),
          escrowTerms: "Standard Enterprise Escrow: 30-day review period with milestone-based releases",
          releaseConditions: "Solution validation by technical review committee, successful pilot implementation, and stakeholder approval"
        }
      }
    });
  };

  const generateDescription = (title: string, domain: string) => {
    const descriptions = {
      "Improving Lost Circulation Management in Unconventional Wells": "Seeking innovative approaches to minimize drilling fluid losses in unconventional formations, focusing on preventive measures and real-time response protocols to maintain wellbore stability and reduce operational costs.",
      "Call for Lessons Learned: Kick Detection and Well Control Events": "Collecting field-tested methodologies for early kick detection and effective well control response, emphasizing safety protocols, equipment optimization, and crew training best practices.",
      "Optimizing Horizontal Wellbore Cleaning Strategies": "Developing comprehensive solutions for debris removal and wellbore conditioning in horizontal drilling operations, targeting improved completion efficiency and production optimization.",
      "AI-Ready Framework for Real-Time Drilling Performance Metrics": "Creating standardized data collection and analytics frameworks that enable AI-driven optimization of drilling parameters, predictive maintenance, and real-time decision support systems.",
      "Knowledge Base on Enhanced Oil Recovery (EOR) Pilot Learnings": "Compiling proven EOR techniques and pilot project outcomes to guide future implementation decisions, focusing on technical feasibility, economic viability, and environmental considerations.",
      "Seismic-to-Reservoir Workflow Best Practices in Shale Plays": "Establishing integrated workflows that effectively translate seismic data into actionable reservoir characterization for unconventional resource development.",
      "Reducing Non-Productive Time (NPT) Through Workflow Standardization": "Implementing systematic approaches to minimize operational delays through standardized procedures, predictive planning, and cross-functional coordination protocols.",
      "Fracture Stimulation Design Optimization Based on Field Experience": "Leveraging field data and completion analytics to optimize fracturing designs for maximum production while minimizing environmental impact and operational costs.",
      "Knowledge Transfer on Aging Well Re-Entry and Recompletion Techniques": "Developing best practices for safely and economically accessing and enhancing production from mature wells through advanced workover and completion technologies.",
      "Developing a Safety Protocol KB for HPHT Drilling Environments": "Creating comprehensive safety frameworks and emergency response procedures specifically tailored for high-pressure, high-temperature drilling operations.",
      "Leak Detection & Response Procedures for Aging Pipelines": "Implementing advanced monitoring systems and rapid response protocols for early leak detection and containment in aging pipeline infrastructure.",
      "Best Practices for SCADA System Integration Across Assets": "Standardizing supervisory control and data acquisition systems to enable seamless monitoring and control across diverse midstream assets and facilities.",
      "Call for Insights: Pipeline Pigging Optimization Strategies": "Developing efficient pipeline inspection and cleaning protocols using intelligent pigging technologies to maintain operational integrity and regulatory compliance.",
      "Digital Twin Readiness: Pipeline Asset Tagging and Data Standards": "Establishing comprehensive digital infrastructure and data standardization protocols to enable advanced digital twin capabilities for pipeline asset management.",
      "Reducing Downtime in Crude Oil Pumping Stations": "Implementing predictive maintenance strategies and operational optimization techniques to minimize unplanned outages and maximize throughput efficiency.",
      "Knowledge Base on Regulatory Compliance in Cross-Border Transport": "Developing standardized compliance frameworks and documentation systems for international pipeline operations and cross-border energy transport.",
      "Emergency Response Procedures for Tank Farms and Storage Facilities": "Creating comprehensive emergency response protocols and safety systems for bulk storage operations, focusing on incident prevention and rapid containment.",
      "Smart Contract Templates for Vendor Logistics Coordination": "Developing blockchain-based contract automation systems to streamline vendor management and logistics coordination across midstream operations.",
      "Carbon Emission Tracking Protocols in Midstream Operations": "Implementing comprehensive monitoring and reporting systems for greenhouse gas emissions across transportation and storage operations.",
      "Training Content for Field Techs on Valve Inspection and Repair": "Creating standardized training programs and certification protocols for field technicians specializing in critical valve maintenance and repair operations.",
      "Turnaround Optimization: Knowledge Base on Shutdown/Startup Sequences": "Developing systematic approaches to minimize refinery turnaround duration while ensuring safety and equipment integrity during major maintenance events.",
      "Improving Process Safety in Fluid Catalytic Cracking (FCC) Units": "Implementing advanced safety systems and operational protocols to prevent incidents and optimize performance in critical FCC operations.",
      "AI-Driven Yield Optimization for Multi-Product Refineries": "Developing machine learning algorithms and optimization systems to maximize product yields while maintaining quality specifications across diverse refinery operations.",
      "Best Practices for Hydrogen Blending in Natural Gas Streams": "Establishing technical standards and operational procedures for safe and efficient hydrogen blending in existing natural gas infrastructure.",
      "Emission Reduction Protocols for Flares and Vents": "Implementing advanced emission control technologies and operational strategies to minimize environmental impact from routine and emergency emissions.",
      "Maintenance Knowledge Base for High-Temperature Heat Exchangers": "Developing specialized maintenance protocols and material selection guidelines for heat exchanger systems operating under extreme thermal conditions.",
      "Digital SOPs for Refinery Lab Testing and QA/QC": "Creating digital workflow systems and automated quality control procedures for laboratory operations and product quality assurance.",
      "Call for Solutions: AI-Based Root Cause Analysis for Refinery Incidents": "Developing artificial intelligence systems capable of analyzing incident data to identify root causes and predict potential failure modes.",
      "Retail Fuel Distribution Optimization in High-Demand Regions": "Implementing supply chain optimization and demand forecasting systems to ensure reliable fuel availability in high-consumption markets.",
      "KB for Training Station Operators on EV and Traditional Fuel Coexistence": "Creating comprehensive training programs for retail operators managing the transition to electric vehicle infrastructure alongside traditional fuel services."
    };
    
    return descriptions[title] || `Comprehensive solution development for ${title} within ${domain} operations, focusing on technical excellence, operational efficiency, and sustainable practices.`;
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/chevron-channel")}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Chevron Channel
          </Button>
          <h1 className="text-4xl font-bold text-foreground mb-4">
            Oil and Gas Demonstration
          </h1>
          <p className="text-lg text-muted-foreground max-w-4xl mb-6">
            Select a demonstration scenario to see a fully populated Call for Solutions workflow. 
            Each scenario includes detailed requirements, team assignments, and $10,000 bounty allocation.
          </p>
        </div>

        <Card className="mb-8">
          <CardHeader className="pb-6">
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Drill className="h-6 w-6" />
              Calls for Solutions (CfS)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              {upstreamCfS.map((call, index) => (
                <Card key={`upstream-${index}`} className="border border-muted hover:border-primary/20 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex flex-col gap-3">
                      <button 
                        className="text-lg font-semibold text-foreground hover:text-primary transition-colors cursor-pointer text-left"
                        onClick={() => handleCfSClick(call, "Upstream")}
                      >
                        CfS: {call}
                      </button>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <ArrowRight className="h-4 w-4" />
                        <span className="font-medium">Domain:</span>
                        <span>Upstream - Exploration, Drilling, and Production</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {midstreamCfS.map((call, index) => (
                <Card key={`midstream-${index}`} className="border border-muted hover:border-primary/20 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex flex-col gap-3">
                      <button 
                        className="text-lg font-semibold text-foreground hover:text-primary transition-colors cursor-pointer text-left"
                        onClick={() => handleCfSClick(call, "Midstream")}
                      >
                        CfS: {call}
                      </button>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <ArrowRight className="h-4 w-4" />
                        <span className="font-medium">Domain:</span>
                        <span>Midstream - Transportation, Storage, and Logistics</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {downstreamCfS.map((call, index) => (
                <Card key={`downstream-${index}`} className="border border-muted hover:border-primary/20 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex flex-col gap-3">
                      <button 
                        className="text-lg font-semibold text-foreground hover:text-primary transition-colors cursor-pointer text-left"
                        onClick={() => handleCfSClick(call, "Downstream")}
                      >
                        CfS: {call}
                      </button>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <ArrowRight className="h-4 w-4" />
                        <span className="font-medium">Domain:</span>
                        <span>Downstream - Refining, Distribution, and Retail</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};